import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Loader2, CheckCircle2, ShieldCheck, ArrowRight, ArrowLeft,
  Building2, MapPin, Landmark, BadgeCheck, Hash,
} from 'lucide-react';
import { toast } from 'sonner';
import { agencyUpdateAPI } from '@/lib/api';
import { US_STATES, formatPhoneInput } from '@/lib/utils';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';

const NAVY = '#0b1e39';
const GOLD = '#d4a244';

const STEPS = [
  { id: 'identity', title: 'Business IDs', icon: Hash },
  { id: 'contact', title: 'Contact & Address', icon: MapPin },
  { id: 'banking', title: 'Banking', icon: Landmark },
  { id: 'review', title: 'Review & Save', icon: BadgeCheck },
];

const Page = ({ children }) => (
  <div className="min-h-screen w-full relative flex items-center justify-center p-4 sm:p-6 overflow-hidden" style={{ background: '#f6f7fa' }}>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage: 'url(/brand/kr-logo.webp)',
        backgroundRepeat: 'repeat',
        backgroundSize: '300px auto',
        backgroundPosition: 'center top',
        opacity: 0.13,
      }}
    />
    <div className="w-full max-w-2xl relative z-10">{children}</div>
  </div>
);

const Header = ({ compact }) => (
  <div
    className="flex items-center justify-between px-6 sm:px-9 py-6 rounded-t-2xl"
    style={{ background: `linear-gradient(135deg, ${NAVY}, #14315c)`, borderBottom: `3px solid ${GOLD}` }}
  >
    <div>
      <div className="text-[11px] font-bold tracking-[0.2em] uppercase" style={{ color: GOLD }}>KR Enterprise Group</div>
      <div className="text-white text-lg font-semibold mt-0.5">Update Agency Information</div>
    </div>
    {!compact && (
      <div className="hidden sm:flex items-center gap-1.5 rounded-full px-3 py-1" style={{ background: 'rgba(212,162,68,0.14)', border: `1px solid ${GOLD}55` }}>
        <ShieldCheck size={13} style={{ color: GOLD }} />
        <span className="text-[11px] font-semibold" style={{ color: GOLD }}>256-bit Secure</span>
      </div>
    )}
  </div>
);

const Card = ({ children }) => (
  <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 24px 60px rgba(11,30,57,0.10)' }}>
    {children}
  </div>
);

export default function AgencyInfoUpdatePage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);

  const [form, setForm] = useState({
    npn: '', ein: '', email: '', phone: '',
    address_line1: '', address_line2: '', city: '', state: '', zip: '',
    bank_name: '', account_holder_name: '', account_type: 'Checking',
    routing_number: '', account_number: '',
  });
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    agencyUpdateAPI.getPublic(token)
      .then((r) => {
        const d = r.data || {};
        setInfo(d);
        setForm((p) => ({
          ...p,
          npn: d.npn || '',
          ein: d.ein || '',
          email: d.email || '',
          phone: d.phone || '',
          address_line1: d.address_line1 || '',
          address_line2: d.address_line2 || '',
          city: d.city || '',
          state: d.state || '',
          zip: d.zip || '',
          account_holder_name: d.banking?.account_holder_name || d.agency_name || '',
          bank_name: d.banking?.bank_name || '',
          account_type: d.banking?.account_type || 'Checking',
        }));
      })
      .catch((e) => setLinkError(e?.response?.data?.detail || 'This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const validateStep = (s) => {
    if (s === 1) {
      if (form.email && !/.+@.+\..+/.test(form.email)) return 'Enter a valid email address';
      if (!form.address_line1.trim() || !form.city.trim() || !form.state || !form.zip.trim()) return 'A complete address is required';
    }
    if (s === 2) {
      const r = form.routing_number.replace(/\D/g, '');
      const a = form.account_number.replace(/\D/g, '');
      if (r || a) {
        if (r.length !== 9) return 'Routing number must be 9 digits (or leave banking blank)';
        if (a.length < 4) return 'Enter a valid bank account number (or leave banking blank)';
      }
    }
    return null;
  };

  const next = () => {
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    setDir(1); setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => { setDir(-1); setStep((s) => Math.max(s - 1, 0)); };

  const handleSubmit = async () => {
    for (let s = 0; s <= 2; s += 1) {
      const err = validateStep(s);
      if (err) { toast.error(err); setDir(-1); setStep(s); return; }
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.routing_number.replace(/\D/g, '') && !payload.account_number.replace(/\D/g, '')) {
        delete payload.routing_number;
        delete payload.account_number;
        delete payload.bank_name;
        delete payload.account_holder_name;
        delete payload.account_type;
      }
      await agencyUpdateAPI.submitPublic(token, payload);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Page><Card><Header compact /><div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="animate-spin mr-2" /> Loading agency details…</div></Card></Page>;
  }

  if (linkError) {
    return (
      <Page><Card><Header compact />
        <div className="text-center px-6 py-14" data-testid="agency-update-link-error">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full flex items-center justify-center" style={{ background: '#fdf6e3' }}>
            <ShieldCheck className="text-amber-500" size={30} />
          </div>
          <h2 className="text-xl font-semibold" style={{ color: NAVY }}>Link unavailable</h2>
          <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">{linkError}</p>
          <p className="text-xs text-slate-400 mt-5">Please contact KR Enterprise Group to request a new link.</p>
        </div>
      </Card></Page>
    );
  }

  if (done) {
    return (
      <Page><Card><Header compact />
        <div className="text-center px-6 py-14" data-testid="agency-update-success">
          <motion.div
            initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 12 }}
            className="mx-auto mb-5 h-20 w-20 rounded-full flex items-center justify-center relative"
            style={{ background: NAVY }}
          >
            <span className="absolute inset-0 rounded-full animate-ping" style={{ background: `${GOLD}40` }} />
            <CheckCircle2 className="relative" size={40} style={{ color: GOLD }} />
          </motion.div>
          <h2 className="text-2xl font-semibold" style={{ color: NAVY }}>Thank you!</h2>
          <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
            {info?.agency_name || 'The agency'}'s information has been updated securely.
            You can safely close this window.
          </p>
        </div>
      </Card></Page>
    );
  }

  const CurrentIcon = STEPS[step].icon;
  const bankOnFile = info?.banking && (info.banking.account_last4 || info.banking.routing_last4);

  return (
    <Page>
      <Card>
        <Header />

        <div className="px-6 sm:px-9 pt-6">
          <div className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <Building2 size={16} className="text-slate-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Agency</div>
              <div className="text-sm font-medium text-slate-800 truncate" data-testid="agency-update-name">{info?.agency_name || '—'}</div>
            </div>
          </div>
        </div>

        {/* Stepper */}
        <div className="px-6 sm:px-9 pt-6" data-testid="agency-update-stepper">
          <div className="flex items-center">
            {STEPS.map((st, i) => {
              const StIcon = st.icon;
              const active = i === step;
              const complete = i < step;
              return (
                <React.Fragment key={st.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className="h-9 w-9 rounded-full flex items-center justify-center transition-all"
                      style={{
                        background: complete ? '#15794a' : active ? NAVY : '#eef1f6',
                        boxShadow: active ? `0 0 0 4px ${GOLD}33` : 'none',
                        color: complete || active ? '#fff' : '#9aa6b8',
                      }}
                    >
                      {complete ? <CheckCircle2 size={16} /> : <StIcon size={15} />}
                    </div>
                    <span className="hidden sm:block text-[10px] mt-1.5 font-medium" style={{ color: active ? NAVY : '#9aa6b8' }}>{st.title}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="flex-1 h-0.5 mx-1.5 rounded" style={{ background: i < step ? '#15794a' : '#e5e9f0' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step body */}
        <div className="px-6 sm:px-9 py-7 min-h-[320px]">
          <div className="flex items-center gap-2 mb-5">
            <CurrentIcon size={18} style={{ color: GOLD }} />
            <h3 className="text-lg font-semibold" style={{ color: NAVY }}>{STEPS[step].title}</h3>
          </div>

          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              initial={{ opacity: 0, x: dir * 28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -28 }}
              transition={{ duration: 0.25 }}
              className="space-y-4"
            >
              {step === 0 && (
                <>
                  <p className="text-sm text-slate-500">Your agency's identifiers are pre-filled — please confirm or correct them.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="npn">Agency NPN</Label>
                      <Input id="npn" value={form.npn} onChange={(e) => setField('npn', e.target.value)} placeholder="Agency NPN" data-testid="aup-npn" />
                    </div>
                    <div>
                      <Label htmlFor="ein">EIN</Label>
                      <Input id="ein" value={form.ein} onChange={(e) => setField('ein', e.target.value)} placeholder="XX-XXXXXXX" data-testid="aup-ein" />
                    </div>
                  </div>
                </>
              )}

              {step === 1 && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="agency@example.com" data-testid="aup-email" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" value={formatPhoneInput(form.phone)} onChange={(e) => setField('phone', e.target.value)} placeholder="(863) 555-1234" data-testid="aup-phone" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="a1">Street address *</Label>
                    <AddressAutocomplete
                      value={form.address_line1}
                      onChange={(v) => setField('address_line1', v)}
                      onPlaceSelected={(p) => setForm((prev) => ({
                        ...prev,
                        address_line1: p.street || prev.address_line1,
                        city: p.city || prev.city,
                        state: p.state || prev.state,
                        zip: p.zip || prev.zip,
                      }))}
                      placeholder="Start typing the address…"
                      testId="aup-address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="a2">Suite / Unit (optional)</Label>
                    <Input id="a2" value={form.address_line2} onChange={(e) => setField('address_line2', e.target.value)} data-testid="aup-address2" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <Label htmlFor="city">City *</Label>
                      <Input id="city" value={form.city} onChange={(e) => setField('city', e.target.value)} data-testid="aup-city" />
                    </div>
                    <div>
                      <Label>State *</Label>
                      <Select value={form.state} onValueChange={(v) => setField('state', v)}>
                        <SelectTrigger data-testid="aup-state"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="zip">ZIP *</Label>
                      <Input id="zip" value={form.zip} onChange={(e) => setField('zip', e.target.value)} data-testid="aup-zip" />
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="flex items-center gap-2 rounded-lg px-3 py-2 mb-1" style={{ background: '#f6fbf7', border: '1px solid #cbe8d6' }}>
                    <ShieldCheck size={15} style={{ color: '#15794a' }} />
                    <span className="text-xs font-medium" style={{ color: '#15794a' }}>Banking is encrypted and sent to KR for review before any payout.</span>
                  </div>
                  {bankOnFile && (
                    <p className="text-[11px] text-slate-400">
                      On file: {info.banking.bank_name || 'bank'} · routing {info.banking.routing_last4 || '••••'} · account {info.banking.account_last4 || '••••'}
                      {info.banking.status ? ` (${info.banking.status})` : ''}. Leave blank to keep it unchanged.
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Account holder name</Label>
                      <Input value={form.account_holder_name} onChange={(e) => setField('account_holder_name', e.target.value)} data-testid="aup-holder" />
                    </div>
                    <div>
                      <Label>Bank name</Label>
                      <Input value={form.bank_name} onChange={(e) => setField('bank_name', e.target.value)} data-testid="aup-bank" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Account type</Label>
                      <Select value={form.account_type} onValueChange={(v) => setField('account_type', v)}>
                        <SelectTrigger data-testid="aup-acct-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Checking">Checking</SelectItem>
                          <SelectItem value="Savings">Savings</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Routing number</Label>
                      <Input value={form.routing_number} onChange={(e) => setField('routing_number', e.target.value)} inputMode="numeric" placeholder="9 digits" data-testid="aup-routing" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                    </div>
                    <div>
                      <Label>Account number</Label>
                      <Input value={form.account_number} onChange={(e) => setField('account_number', e.target.value)} inputMode="numeric" data-testid="aup-account" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400">Banking is optional — leave it blank if nothing has changed.</p>
                </>
              )}

              {step === 3 && (
                <div className="space-y-3" data-testid="aup-review">
                  <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
                    {[
                      ['NPN', form.npn || '—'],
                      ['EIN', form.ein || '—'],
                      ['Email', form.email || '—'],
                      ['Phone', form.phone || '—'],
                      ['Address', [form.address_line1, form.address_line2, `${form.city}, ${form.state} ${form.zip}`].filter(Boolean).join(', ')],
                      ['Banking', form.account_number.replace(/\D/g, '')
                        ? `${form.bank_name || 'Bank'} · ****${form.account_number.replace(/\D/g, '').slice(-4)} (updated — pending review)`
                        : (bankOnFile ? 'Unchanged' : 'Not provided')],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 px-4 py-2.5 text-sm">
                        <span className="text-slate-400">{k}</span>
                        <span className="font-medium text-slate-800 text-right truncate">{v}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400">By submitting, you confirm this information is accurate and up to date.</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer nav */}
        <div className="px-6 sm:px-9 py-5 border-t border-slate-100 flex items-center justify-between">
          <Button variant="ghost" onClick={back} disabled={step === 0 || submitting} data-testid="aup-back" className="text-slate-500">
            <ArrowLeft size={15} className="mr-1" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="text-white" style={{ background: NAVY }} data-testid="aup-next">
              Continue <ArrowRight size={15} className="ml-1.5" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="text-white" style={{ background: '#15794a' }} data-testid="aup-submit">
              {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <BadgeCheck size={16} className="mr-1.5" />}
              Save Agency Information
            </Button>
          )}
        </div>
      </Card>
      <p className="text-center text-[11px] text-slate-400 mt-4">© KR Enterprise Group · Confidential &amp; secure</p>
    </Page>
  );
}
